// AM-029 — لایهٔ موقت React Query (طبق الگوی features/shopfloor/api.ts).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance as apiClient } from "@/api/client";

import type { OperatorStationAssignment, StationAssignmentOverride, StationResolutionResult } from "./types";

const BASE = "/api/v1/production/station-assignment";

export function useOperatorStationAssignments(operatorUserId?: string) {
  return useQuery({
    queryKey: ["operator-station-assignments", operatorUserId],
    queryFn: async () => {
      const res = (
        await apiClient.get<any>(`${BASE}/operator-station-assignments/`, {
          params: operatorUserId ? { operator_user_id: operatorUserId } : undefined,
        })
      ).data;
      return res.results || res;
    },
  });
}

export function useCreateStationAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      operator_user_id: string;
      mode: "sole" | "concurrent" | "rotational";
      start_date: string;
      end_date?: string | null;
      target_work_center?: string;
      target_machine?: string;
      roster?: string;
      shift_template?: string;
    }) => (await apiClient.post<OperatorStationAssignment>(`${BASE}/operator-station-assignments/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operator-station-assignments"] }),
  });
}

export function useAddRotationSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      assignment: string;
      sequence_no: number;
      relative_start: string;
      relative_end: string;
      target_work_center?: string;
      target_machine?: string;
    }) => (await apiClient.post(`${BASE}/operator-station-rotation-slots/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operator-station-assignments"] }),
  });
}

export function useResolveStation(operatorUserId: string, date: string, atTime?: string, shiftStart?: string, enabled = true) {
  return useQuery({
    queryKey: ["resolve-station", operatorUserId, date, atTime, shiftStart],
    enabled: enabled && !!operatorUserId && !!date,
    queryFn: async () =>
      (
        await apiClient.get<StationResolutionResult[]>(`${BASE}/operator-station-assignments/resolve/`, {
          params: { operator_user_id: operatorUserId, date, at_time: atTime, shift_start: shiftStart },
        })
      ).data,
  });
}

export function useCreateStationOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      operator_user_id: string;
      date: string;
      target_work_center?: string;
      target_machine?: string;
      reason?: string;
    }) => (await apiClient.post<StationAssignmentOverride>(`${BASE}/station-assignment-overrides/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resolve-station"] }),
  });
}
